import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Trash, PencilSimple, Check, Brain } from "@phosphor-icons/react";

const VOICES = ["nova", "shimmer", "coral"];

export const AssistantMemory = () => {
  const { user } = useAuth();
  const isMain = user?.role === "admin";
  const [all, setAll] = useState(false);
  const [rows, setRows] = useState([]);
  const [text, setText] = useState("");
  const [edit, setEdit] = useState(null);
  const [voice, setVoice] = useState("nova");

  const load = useCallback(() => api.get("/assistant/memory", { params: { all_users: all } }).then(({ data }) => setRows(data)).catch(() => {}), [all]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/assistant/voice").then(({ data }) => setVoice(data.voice)).catch(() => {}); }, []);

  const run = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const add = () => text.trim() && run(() => api.post("/assistant/memory", { text }), "Yaad rakh liya").then(() => setText(""));
  const save = () => run(() => api.put(`/assistant/memory/${edit.id}`, { text: edit.text }), "Badal diya").then(() => setEdit(null));
  const forget = () => window.confirm("Saari memory mita dein?") && run(() => api.post("/assistant/memory/forget-all"), "Sab bhool gayi");
  const setV = (v) => run(() => api.put("/assistant/voice", { voice: v }), `Awaaz: ${v}`).then(() => setVoice(v));

  return (
    <div className="space-y-4" data-testid="assistant-memory">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-black text-slate-100 flex items-center gap-2"><Brain size={20} className="text-orange-400" /> HEY SHOBHIT MEMORY</h3>
        <div className="flex gap-2">
          {isMain && (
            <Button size="sm" variant="outline" onClick={() => setAll((a) => !a)} className="rounded-sm border-slate-700 bg-slate-950" data-testid="memory-toggle-all">
              {all ? "Sirf meri" : "Sab users ki"}
            </Button>
          )}
          <Button size="sm" onClick={forget} className="rounded-sm bg-red-600 hover:bg-red-700 text-white" data-testid="memory-forget-all">SAB BHOOL JAO</Button>
        </div>
      </div>
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Nayi baat yaad karwao…" className="rounded-sm bg-slate-950 border-slate-700" data-testid="memory-add-input" />
        <Button onClick={add} className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white" data-testid="memory-add-btn">ADD</Button>
      </div>
      <div className="border border-slate-800 rounded-sm divide-y divide-slate-800">
        {rows.length === 0 && <div className="p-4 text-sm text-slate-400" data-testid="memory-empty">Abhi kuch yaad nahi hai.</div>}
        {rows.map((m) => (
          <div key={m.id} className="p-3 flex items-center gap-2" data-testid={`memory-row-${m.id}`}>
            {edit?.id === m.id ? (
              <Input value={edit.text} onChange={(e) => setEdit({ ...edit, text: e.target.value })} className="h-8 rounded-sm bg-slate-950 border-slate-700" data-testid="memory-edit-input" />
            ) : (
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-100 break-words">{m.text}</div>
                <div className="text-[11px] text-slate-500">{m.user_name} · {(m.updated_at || m.created_at || "").slice(0, 10)}</div>
              </div>
            )}
            {edit?.id === m.id
              ? <button onClick={save} className="text-emerald-400" data-testid="memory-save-btn"><Check size={16} /></button>
              : <button onClick={() => setEdit({ id: m.id, text: m.text })} className="text-slate-400 hover:text-slate-100" data-testid={`memory-edit-${m.id}`}><PencilSimple size={16} /></button>}
            <button onClick={() => run(() => api.delete(`/assistant/memory/${m.id}`), "Mita diya")} className="text-red-400" data-testid={`memory-delete-${m.id}`}><Trash size={16} /></button>
          </div>
        ))}
      </div>
      <div className="text-[11px] text-slate-500">Password, OTP, bank details kabhi save nahi hote. Paisa/order/stock ka jawab hamesha live data se aata hai.</div>
      {isMain && (
        <div className="space-y-2" data-testid="assistant-voice-picker">
          <div className="text-sm font-bold text-slate-200">Awaaz chuno</div>
          {VOICES.map((v) => (
            <div key={v} className="flex items-center gap-3">
              <Button size="sm" variant={voice === v ? "default" : "outline"} onClick={() => setV(v)}
                className={"rounded-sm w-24 " + (voice === v ? "bg-orange-500 text-white" : "border-slate-700 bg-slate-950")} data-testid={`voice-pick-${v}`}>{v}</Button>
              <audio controls preload="none" src={`/voice/sample_${v}.mp3`} className="h-8" data-testid={`voice-sample-${v}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
